<template>
  <tm-avatar-wrapper
    :size="avatarSize"
    class="tm-avatar"
    :class="{
      unread: unread,
      [`tm-avatar--${color}`]: true,
    }"
    :color="color"
    :outlined="outlined"
    :bordered="bordered"
    :exact-color="exactColor"
  >
    <tm-img
      v-if="urlVerifyed"
      class="tm-avatar__img"
      :src="url"
      :alt="name"
    />
    <template v-else>
      <slot>
        <span
          v-if="emoji"
          v-safe-html="emojiToHtml(emoji, { customSize: emojiSize })"
          class="d-flex align-center justify-center"
        />
        <span
          v-else-if="placeholderType === 'symbol' && avatarSymbol"
          :class="`avatar-letter color--${textColor}`"
          :style="`font-size: ${avatarTextSize}; font-weight: ${placeholderFontWeight}`"
        >
          {{ avatarSymbol }}
        </span>
        <tm-icon
          v-else
          :class="customIconClass"
          :name="customIcon"
          :custom-size="avatarIconSize"
          :color="textColor"
        />
      </slot>
    </template>

    <tm-tooltip
      v-if="tooltip || hasTooltipSlot"
      :delay-time="tooltipDelayTime ?? delayOptions.default"
      :delayed="!!tooltipDelayTime"
    >
      <slot name="tooltip">{{ tooltip }}</slot>
    </tm-tooltip>
    <div
      v-if="$slots['status']"
      class="tm-avatar__status-icon"
    >
      <slot name="status" />
    </div>
  </tm-avatar-wrapper>
</template>

<script lang="ts">
import { defineComponent, computed, toRef, type PropType } from '@/composition/vue/compositionApi'
import TmTooltip from '@/components/shared/tooltip/TmTooltip.vue'
import {
  delayOptions,
  avatarSizesMap,
  type AvatarSize,
  type SizeProp,
  type TmAvatarPlaceholder,
} from '@/components/shared/types'
import TmIcon from '@/components/shared/TmIcon.vue'
import TmImg from '@/components/shared/TmImg.vue'
import { isLetterFirst } from '@/utils/string/isLetterFirst'
import type { IconName } from '@/assets/icons/icons'
import TmAvatarWrapper from '@/components/shared/TmAvatarWrapper.vue'
import { type StyleColors, styleColorsMainVariant } from '@/constants/styleColors'
import { useUrlVerified } from '@/composition/avatar'
import { addPxToNumber } from '@/utils/string/addPxToNumber'
import { emojiToHtml } from '@/utils/emoji/emojiHelpers'
import type { EmojiNative } from '@/services/forms/tmTiptap/types'

export default defineComponent({
  components: {
    TmAvatarWrapper,
    TmImg,
    TmIcon,
    TmTooltip,
  },
  props: {
    url: {
      type: String,
      default: '',
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String as SizeProp<AvatarSize>,
      default: 'medium',
    },
    placeholderType: {
      type: String as PropType<TmAvatarPlaceholder>,
      default: 'symbol',
    },
    placeholderCustomSize: {
      type: String,
    },
    placeholderFontWeight: {
      type: String,
    },
    customSize: {
      type: String,
    },
    customIcon: {
      type: String as PropType<IconName>,
      default: 'person' satisfies IconName,
    },
    customIconClass: {
      type: String,
    },
    color: {
      type: String as PropType<StyleColors>,
      default: 'gray',
    },
    name: {
      type: String,
    },
    email: {
      type: String,
    },
    initials: {
      type: String,
    },
    unread: {
      type: Boolean,
    },
    tooltip: {
      type: String,
    },
    tooltipDelayTime: {
      type: Number,
    },
    bordered: {
      type: Boolean,
    },
    emoji: {
      type: String as PropType<EmojiNative>,
    },
    exactColor: Boolean,
    statusOffsetPx: {
      type: Number,
      default: 0,
    },
    overrideTextColor: {
      type: String as PropType<StyleColors>,
    },
  },
  setup(props, context) {
    const url = toRef(props, 'url')
    const { urlVerifyed } = useUrlVerified(url)

    type SizeObj = {
      [key: string]: string
    }

    const sizesTextMap: SizeObj = {
      xxSmall: '8px',
      xSmall: '9px',
      small: '11px',
      medium: '12px',
      large: '16px',
      xLarge: '18px',
    }

    const sizesIconMap: SizeObj = {
      xxSmall: '10px',
      xSmall: '10px',
      small: '12px',
      medium: '14px',
      large: '18px',
      xLarge: '20px',
    }

    const avatarSize = computed<number | undefined>(() => {
      if (props.customSize) return Number.parseInt(props.customSize, 10) || undefined
      return avatarSizesMap[props.size] || undefined
    })

    const avatarTextSize = computed<string>(() => props.placeholderCustomSize || sizesTextMap[props.size])

    const avatarIconSize = computed<string>(() => props.placeholderCustomSize || sizesIconMap[props.size])

    const avatarSymbol = computed(() => {
      const names = [props.name, props.email, props.initials].join(' ').split(' ')
      const chars = names.map((name) => name[0])
      return chars.find(isLetterFirst)?.toUpperCase() || ''
    })

    const hasTooltipSlot = context.slots.tooltip

    const textColor = computed<StyleColors>(() => {
      if (props.overrideTextColor) {
        return props.overrideTextColor
      }
      const mainColors = [...styleColorsMainVariant] as string[]
      if (mainColors.includes(props.color)) {
        return `${props.color}600` as StyleColors
      }
      return 'white'
    })
    const emojiSize = computed(() => addPxToNumber(avatarSize.value ? avatarSize.value / 2 + 0.5 : 0))

    const statusOffset = computed(() => {
      if (props.statusOffsetPx) return addPxToNumber(-props.statusOffsetPx)
      return props.bordered ? '2px' : '0'
    })

    return {
      avatarSize,
      avatarTextSize,
      avatarIconSize,
      avatarSymbol,
      hasTooltipSlot,
      urlVerifyed,
      textColor,
      emojiSize,
      emojiToHtml,
      statusOffset,
      delayOptions,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.tm-avatar {
  &__img {
    border-radius: inherit;
    width: inherit;
    height: inherit;
  }

  &__status-icon {
    position: absolute;
    bottom: v-bind(statusOffset);
    right: v-bind(statusOffset);
    z-index: 1;
    line-height: 0;
  }
  .avatar-letter {
    line-height: 1;
    font-weight: $semi-bold-font;
  }
  &.unread {
    &:after {
      content: '';
      position: absolute;
      left: -1px;
      top: -1px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: $red;
      border: 2px solid $white;
    }
  }
  img {
    image-rendering: -moz-crisp-edges; /* Firefox */
    image-rendering: -o-crisp-edges; /* Opera */
    image-rendering: -webkit-optimize-contrast; /* Webkit (non-standard naming) */
    image-rendering: crisp-edges;
    -ms-interpolation-mode: nearest-neighbor; /* IE (non-standard property) */
  }
}
</style>
